import { DOCUMENT } from '@angular/common';
import { DestroyRef, Signal, inject, signal } from '@angular/core';

/**
 * Punto de ruptura único donde una tabla deja de caber legible (aunque haya
 * scroll horizontal) y el listado pasa a tarjetas. Lo comparten
 * `templates-page`, `field-types-page`, `users-page` y, por debajo de este
 * mismo archivo, `.ks-filter-bar` en `_components.scss` para apilar sus
 * controles a columna completa.
 */
export const MOBILE_BREAKPOINT_QUERY = '(max-width: 767.98px)';

/**
 * Señal reactiva sobre un `matchMedia`, con alta y baja del listener
 * gestionadas por el `DestroyRef` de quien la invoca.
 *
 * Función de inyección al estilo `takeUntilDestroyed()`: DEBE llamarse en un
 * contexto de inyección activo (inicializador de campo o cuerpo del
 * constructor de un componente/servicio), nunca dentro de un método normal
 * ni de un callback asíncrono — ahí ya no hay contexto de inyección y
 * `inject()` lanzaría en tiempo de ejecución.
 */
export function injectMediaQuery(query: string): Signal<boolean> {
  const document = inject(DOCUMENT);
  const destroyRef = inject(DestroyRef);

  const matches = signal(false);
  const view = document.defaultView;

  if (view?.matchMedia) {
    const mediaQueryList = view.matchMedia(query);
    matches.set(mediaQueryList.matches);

    const onChange = (event: MediaQueryListEvent) => matches.set(event.matches);
    mediaQueryList.addEventListener('change', onChange);
    destroyRef.onDestroy(() => mediaQueryList.removeEventListener('change', onChange));
  }

  return matches.asReadonly();
}

/**
 * Atajo para `MOBILE_BREAKPOINT_QUERY`: la señal que decide si un listado se
 * pinta como tabla (escritorio) o como tarjetas (móvil). Mismas reglas de
 * uso que `injectMediaQuery`.
 */
export function injectIsMobile(): Signal<boolean> {
  return injectMediaQuery(MOBILE_BREAKPOINT_QUERY);
}
